<template>
  <div class="prb-section-container">
    <BuilderHeader
      :active="active"
      :finishedSection="finishedSection"
      :index="index"
      :incomplete="incomplete"
      header="Promotion Info"
      section="promo-info"
      :changeActiveStepIdx="changeActiveStepIdx"
      :toggleInfoModal="showInfo"
    />
    <div
      :class="[
        'pbs-body-container promo-more-info',
        active ? 'active' : null,
      ]"
    >
      <div class="promo-title">
        Action
      </div>
      <div class="promo-dropdown">
        <SACDropdown
          field="actionType"
          :options="actionTypeOptions"
          :value="actionType"
          :change="handleChange"
        />
      </div>
      <div v-if="actionType === 'URL'">
        <div class="promo-title">
          Website Link
        </div>
        <div class="promo-sub">
          Please include http:// or https:// in your URL
        </div>
        <input
          id="website-link"
          v-model="websiteLink"
          class="promo-more-input"
          name="website-link"
        >
      </div>
      <div v-else>
        <div class="pbs-input-container">
          <div class="promo-title">
            Phone Number
          </div>
          <input
            id="promo-phone"
            v-model="phoneNumber"
            class="promo-more-input"
            name="promo-phone"
            @keypress="isNumber"
          >
        </div>
      </div>
      <div class="promo-title">
        More Details
      </div>
      <textarea
        id="more-details"
        v-model="description"
        class="promo-more-textarea"
        name="more-details"
        aria-label="description"
      />
      <div class="pbs-button-container">
        <button
          type="button"
          class="pbs-grey-button"
          :disabled="buttonDisabled"
          @click="handleContinue"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import promoMixin from '@shared/mixins/promoMixin';

import formatPhoneNumber from '@shared/lib/formatter/formatPhoneNumber';
import urlValidation from '@shared/lib/errorCatch/urlValidation';
import phoneValidation from '@shared/lib/errorCatch/phoneValidation';

import SACDropdown from '@shared/components/sac/SACDropdown.vue';
import BuilderHeader from './BuilderHeader.vue';

export default {
  name: 'PromotionInfo',
  components: {
    SACDropdown,
    BuilderHeader,
  },
  mixins: [promoMixin],
  props: {
    index: Number,
    active: Boolean,
    promo: Object,
    changeActiveStepIdx: Function,
    finishedSection: Array,
    updatePromo: Function,
    toggleInfoModal: Function,
    setSectionIncomplete: Function,
  },
  data() {
    return {
      actionTypeOptions: [{
        label: 'Navigate to website',
        value: 'URL',
      }, {
        label: 'Phone call',
        value: 'Call',
      }],
      actionType: 'URL',
      websiteLink: '',
      phone: '',
      description: '',
    };
  },
  computed: {
    incomplete() {
      const {
        actionType,
        actionUri,
      } = this.promo;
      let incomplete = false;
      if (actionType !== this.actionType) {
        incomplete = true;
      }
      if (actionType === 'URL' && actionUri !== this.websiteLink) {
        incomplete = true;
      }
      if (actionType === 'Call' && actionUri !== this.phone) {
        incomplete = true;
      }
      return incomplete;
    },
    phoneNumber: {
      get() {
        return this.phone;
      },
      set(phone) {
        this.phone = formatPhoneNumber(phone);
      },
    },
    buttonDisabled() {
      if (this.actionType === 'URL') {
        return !this.websiteLink || !urlValidation(this.websiteLink);
      }
      if (this.actionType === 'Call') {
        return !this.phoneNumber || !phoneValidation(this.phoneNumber);
      }
      return true;
    },
  },
  watch: {
    promo(pr) {
      if (!this.incomplete || this.finishedSection.includes('promo-info')) {
        if (pr.actionType) {
          this.actionType = pr.actionType;
        } else {
          this.actionType = 'URL';
        }
        this.phoneNumber = pr.actionUri;
        this.websiteLink = pr.actionUri;
        this.description = pr.description;
      }
    },
    actionType(type) {
      if (type !== this.promo?.actionType) {
        this.setSectionIncomplete('promo-info');
      }
    },
    websiteLink(w) {
      if (this.actionType === 'URL' && w !== this.promo?.actionUri) {
        this.setSectionIncomplete('promo-info');
      }
    },
    phoneNumber(p) {
      if (this.actionType === 'Call' && p !== this.promo?.actionUri) {
        this.setSectionIncomplete('promo-info');
      }
    },
    description(desc) {
      if (desc !== this.promo?.description) {
        this.setSectionIncomplete('promo-info');
      }
    },
  },
  methods: {
    handleChange(field, value) {
      this[field] = value;
    },
    handleContinue() {
      this.changeActiveStepIdx(this.index + 1);
      const newPromo = {
        ...this.promo,
        actionType: this.actionType,
        actionUri: this.actionType === 'URL' ? this.websiteLink : this.phoneNumber,
        description: this.description,
        actionText: this.actionType === 'URL' ? 'Claim offer now' : 'Call now',
      };
      this.updatePromo(newPromo, 'promo-info');
    },
    isNumber(evt) {
      const event = evt || window.event;
      if (event.target.value.length > 11) {
        event.preventDefault();
      }
      const charCode = (event.which) ? event.which : event.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        event.preventDefault();
      } else {
        return true;
      }
    },
    showInfo() {
      this.toggleInfoModal(true, 'promotion-info');
    },
  },
};
</script>
<style lang="scss">

.prb-section-container {
  .promo-dropdown {
    width: 50%;
    margin-bottom: 12px;
  }
  .promo-title {
    font-size: 0.875em;
    color: $NBLUE110;
    font-weight: 500;
    margin-right: 4px;
  }
  .promo-sub {
    font-size: 0.75rem;
    color: $N90;
    font-weight: 500;
  }
  .promo-more-textarea {
    padding-left: 10px;
    padding-right: 10px;
    width: 100%;
    height: 120px;
    border: 1px solid $N10;
    border-radius: 4px;
  }
  .promo-more-input {
    border: 1px solid $N10;
    border-radius: 4px;
    width: 70%;
    height: 28px;
    padding-left: 4px;
    font-size: 0.75em;
    margin-bottom: 12px;
    &.width-20 {
      width: 20%;
    }
  }
}
</style>
